import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, MessageSquare, Bell, User, Settings, Award, LogOut, BarChart2 } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import Logo from './Logo';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { getUnreadNotificationCount, getUnreadMessageCount, subscribeToNotifications, subscribeToMessages } from "@/lib/supabase";
import { registerExperienceListener } from '@/lib/experienceManager';

const menuItems = [
  { icon: Home, text: 'Home', path: '/home' },
  { icon: Award, text: 'Leaderboard', path: '/leaderboard' },
  { icon: MessageSquare, text: 'Messages', path: '/messages' },
  { icon: Bell, text: 'Notifications', path: '/notifications' },
  { icon: BarChart2, text: 'Analytics', path: '/analytics' },
  { icon: User, text: 'Profile', path: '/profile' },
];

interface SidebarProps {
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

const Sidebar = ({ open = false, setOpen }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, profile, user } = useAuth();
  const { toast } = useToast();
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [userLevel, setUserLevel] = useState<number | undefined>(undefined);
  
  // Close sidebar when navigating on mobile
  const handleNavigate = (path: string) => {
    if (setOpen && window.innerWidth < 768) {
      setOpen(false);
    }
    navigate(path);
  };
  
  // Fetch unread notification count and set up real-time subscription
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (!user) return;
      
      try {
        const result = await getUnreadNotificationCount(user.id);
        // Check if result is a number, otherwise handle as object with count property
        if (typeof result === 'number') {
          setNotificationCount(result);
        } else if (result && typeof result === 'object' && 'count' in result) {
          setNotificationCount((result as { count: number }).count || 0);
        } else {
          console.error("Unexpected notification count format:", result);
          setNotificationCount(0);
        }
      } catch (error) {
        console.error("Error fetching notification count:", error);
      }
    };
    
    fetchNotificationCount();
    
    // Set up a real-time subscription for new notifications
    let unsubscribe: (() => void) | null = null;
    
    if (user) {
      unsubscribe = subscribeToNotifications(user.id, (notification) => {
        console.log('Received real-time notification:', notification);
        
        // Only increment if the notification is unread
        if (!notification.is_read) {
          // Increment notification count when new notification arrives
          setNotificationCount(prev => prev + 1);
          
          // Show a toast notification
          toast({
            title: `New ${notification.type} notification`,
            description: notification.content,
            duration: 5000,
          });
        }
      });
      
      console.log('Notification subscription set up for user:', user.id);
      
      // Listen for sign out event
      const handlePreSignout = () => {
        console.log('Sidebar component: Cleaning up subscriptions before sign out');
        if (unsubscribe) {
          unsubscribe();
        }
      };
      
      window.addEventListener('app:pre-signout', handlePreSignout);
      
      // Set up a polling interval as a fallback for missed real-time events
      const interval = setInterval(fetchNotificationCount, 60000);
      
      // Clean up function
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
        clearInterval(interval);
        window.removeEventListener('app:pre-signout', handlePreSignout);
      };
    }
    
    return undefined;
  }, [user, toast]);

  // Fetch unread message count and set up real-time subscription for messages
  useEffect(() => {
    const fetchMessageCount = async () => {
      if (!user) return;
      
      try {
        const count = await getUnreadMessageCount(user.id);
        setMessageCount(count);
      } catch (error) {
        console.error("Error fetching message count:", error);
      }
    };
    
    fetchMessageCount();
    
    // Set up real-time subscription for new messages
    let unsubscribeMessages: (() => void) | null = null;
    
    if (user) {
      unsubscribeMessages = subscribeToMessages(user.id, (newMessage) => {
        console.log('Received real-time message:', newMessage);
        
        // Only increment count if this is a new unread message sent to this user
        if (!newMessage.is_read && newMessage.receiver_id === user.id) {
          setMessageCount(prev => prev + 1);
          
          // Show a toast notification for new message
          toast({
            title: "New Message",
            description: `${newMessage.sender?.display_name || newMessage.sender?.username || 'Someone'} sent you a message.`,
            duration: 5000,
          });
        }
      });
      
      // Listen for sign out event
      const handlePreSignout = () => {
        console.log('Sidebar component: Cleaning up message subscription before sign out');
        if (unsubscribeMessages) {
          unsubscribeMessages();
        }
      };
      
      window.addEventListener('app:pre-signout', handlePreSignout);
      
      // Set up a polling interval as a fallback
      const interval = setInterval(fetchMessageCount, 60000);
      
      // Clean up function
      return () => {
        if (unsubscribeMessages) {
          unsubscribeMessages();
        }
        clearInterval(interval);
        window.removeEventListener('app:pre-signout', handlePreSignout);
      };
    }
    
    return undefined;
  }, [user, toast]);
  
  // Listen for location changes to refresh counts
  useEffect(() => {
    // Only fetch when navigating away from the respective pages
    if (user) {
      if (!location.pathname.includes('/notifications')) {
        const fetchNotificationCount = async () => {
          try {
            const result = await getUnreadNotificationCount(user.id);
            if (typeof result === 'number') {
              setNotificationCount(result);
            } else if (result && typeof result === 'object' && 'count' in result) {
              setNotificationCount((result as { count: number }).count || 0);
            }
          } catch (error) {
            console.error("Error fetching notification count after navigation:", error);
          }
        };
        
        fetchNotificationCount();
      }
      
      if (!location.pathname.includes('/messages')) {
        const fetchMessageCount = async () => {
          try {
            const count = await getUnreadMessageCount(user.id);
            setMessageCount(count);
          } catch (error) {
            console.error("Error fetching message count after navigation:", error);
          }
        };
        
        fetchMessageCount();
      }
    }
  }, [location.pathname, user]);
  
  // Listen for experience updates
  useEffect(() => {
    if (!user?.id) return;
    
    // Initialize from profile
    setUserLevel(profile?.level || 1);
    
    // Register for experience updates
    const unregister = registerExperienceListener((userId, _experience, level) => {
      if (userId === user.id) {
        setUserLevel(level);
      }
    });
    
    return unregister;
  }, [user?.id, profile?.level]);
  
  // Update userLevel when profile changes
  useEffect(() => {
    if (profile?.level) {
      setUserLevel(profile.level);
    }
  }, [profile?.level]);
  
  const handleSignOut = async () => {
    try {
      // Show toast first
      toast({
        title: "Signing out...",
        description: "Please wait while we sign you out",
      });
      
      // Small delay to ensure the toast is shown
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Trigger a custom event to notify components to clean up
      window.dispatchEvent(new CustomEvent('app:pre-signout'));
      
      // Another small delay to allow cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Perform the sign out
      await signOut();
      
      // Use window.location instead of navigate for a fresh page load
      window.location.href = '/login';
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Sign out failed",
        description: "There was a problem signing you out. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  // Mobile bottom navigation (visible on small screens only)
  const MobileNav = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-vibebops-card border-t-4 border-black z-20 px-2 py-3">
      <div className="flex justify-around items-center">
        {menuItems.slice(0, 5).map((item) => (
          <button
            key={item.text}
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center p-1 ${
              location.pathname === item.path ? 'text-vibebops-accent' : 'text-vibebops-text'
            }`}
          >
            <div className="relative">
              <item.icon className="h-6 w-6" />
              {item.text === 'Notifications' && notificationCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-vibebops-accent text-white text-xs rounded-full h-4 w-4 flex items-center justify-center border-2 border-black">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
              {item.text === 'Messages' && messageCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center border-2 border-black">
                  {messageCount > 9 ? '9+' : messageCount}
                </span>
              )}
            </div>
            <span className="text-xs mt-1 font-bold">{item.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
  
  return (
    <>
      {/* Desktop Sidebar (always visible on md+ screens) */}
      <div className={`
        h-screen fixed left-0 top-0 bg-vibebops-card flex flex-col border-r-4 border-black z-20
        md:w-60 md:translate-x-0 transition-transform duration-300 ease-in-out
        ${open ? 'w-64 translate-x-0' : 'w-64 -translate-x-full'}
      `}>
        <div className="p-4 border-b-4 border-black">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
            <h1 className="text-xl font-extrabold">VibeBops</h1>
          </Link>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-2">
            <p className="text-xs font-extrabold text-vibebops-textMuted mb-2 uppercase px-3">General</p>
            <nav className="space-y-1">
              {menuItems.map((item) => (
                <Link 
                  key={item.text} 
                  to={item.path}
                  onClick={() => setOpen && setOpen(false)}
                  className={`menu-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-bold">{item.text}</span>
                  {item.text === 'Notifications' && notificationCount > 0 && (
                    <span className="ml-auto bg-vibebops-accent text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-black">
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                  {item.text === 'Messages' && messageCount > 0 && (
                    <span className="ml-auto bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-black">
                      {messageCount > 99 ? '99+' : messageCount}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </div>
        
        <div className="p-3 border-t-4 border-black">
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full focus:outline-none">
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-vibebops-highlight border-2 border-black transition-all hover:translate-y-1 hover:translate-x-1 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                <Avatar className="h-8 w-8 border-2 border-black">
                  <AvatarImage src={profile?.avatar_url || profile?.avatar || '/placeholder.svg'} alt={profile?.display_name || "User"} />
                  <AvatarFallback className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold">
                    {profile?.display_name?.charAt(0) || profile?.username?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-extrabold truncate">{profile?.display_name || "User"}</p>
                  <p className="text-xs text-vibebops-textMuted truncate font-bold">Level {userLevel || 1}</p>
                </div>
                <Settings className="h-4 w-4 text-vibebops-textMuted" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-52 border-3 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <DropdownMenuItem className="font-bold cursor-pointer" asChild>
                <Link to="/profile" className="flex items-center gap-2" onClick={() => setOpen && setOpen(false)}>
                  <User className="h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="font-bold cursor-pointer" asChild>
                <Link to="/settings" className="flex items-center gap-2" onClick={() => setOpen && setOpen(false)}>
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="font-bold cursor-pointer flex items-center gap-2"
                onClick={handleSignOut}
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNav />
      
      {/* Overlay for mobile when sidebar is open */}
      {open && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
          onClick={() => setOpen && setOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
